import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Truck, MessagesSquare, Smartphone, Leaf, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/product/ProductCard';
import { productsAPI, brandsAPI } from '@/utils/api';

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [grainFree, setGrainFree] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [f, b, br, gf] = await Promise.all([
          productsAPI.getFeatured(),
          productsAPI.getBestsellers(),
          brandsAPI.getAll(),
          productsAPI.getAll({ is_grain_free: true, limit: 4 })
        ]);
        setFeatured(f);
        setBestsellers(b);
        setBrands(br);
        setGrainFree(gf);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const needs = [
    { label: 'Cachorros', slug: 'cachorro', icon: '🐶', filter: 'life_stage=cachorro' },
    { label: 'Adultos', slug: 'adulto', icon: '🦴', filter: 'life_stage=adulto' },
    { label: 'Senior', slug: 'senior', icon: '💚', filter: 'life_stage=senior' },
    { label: 'Piel y pelaje', slug: 'piel-pelaje', icon: '✨', filter: 'protein=salmon' },
    { label: 'Control de peso', slug: 'peso', icon: '⚖️', filter: 'is_specialized=true' },
    { label: 'Libre de granos', slug: 'grain-free', icon: '🌾', filter: 'is_grain_free=true' },
    { label: 'Especializado', slug: 'especializado', icon: '🩺', filter: 'is_specialized=true' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F7F2E8] via-[#FDFBF7] to-[#F7F2E8]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-4">
                Nutripetgourmet — Nutrición premium
              </p>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-[#1C1917] mb-6">
                Nutrición que se nota en <span className="text-[#2A5C43]">cada etapa</span> de su vida
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed mb-8 max-w-lg">
                Alimentos premium, holísticos y especializados para cuidar a tu mascota todos los días. Con IVA incluido y envíos a toda Colombia.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  asChild
                  className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-8 py-6 text-sm font-semibold"
                  data-testid="hero-shop-dogs-btn"
                >
                  <Link to="/perros">
                    Comprar para perros <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-[#2A5C43] text-[#2A5C43] hover:bg-[#2A5C43] hover:text-white rounded-full px-8 py-6 text-sm font-semibold"
                  data-testid="hero-find-food-btn"
                >
                  <Link to="/encuentra-alimento">Encontrar el alimento ideal</Link>
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-10">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#2A5C43]" />
                  <span className="text-xs text-[#57534E]">Marcas premium</span>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#2A5C43]" />
                  <span className="text-xs text-[#57534E]">Envíos nacionales</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#2A5C43]" />
                  <span className="text-xs text-[#57534E]">Pago seguro</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1768676758489-851bb9ea865a?crop=entropy&cs=srgb&fm=jpg&w=800"
                  alt="Golden retriever feliz al atardecer"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-[#E7E5E4] hidden md:block">
                <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-1">Especializado</p>
                <p className="font-display font-bold text-lg text-[#1C1917] leading-tight">Consulta gratuita</p>
                <p className="text-xs text-[#57534E]">con nuestros expertos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by need */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-3">Compra por necesidad</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight">
              Encuentra según su etapa de vida
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {needs.map((need) => (
              <Link
                key={need.slug}
                to={`/perros?${need.filter}`}
                data-testid={`need-${need.slug}`}
                className="group bg-white rounded-2xl border border-[#E7E5E4] p-6 text-center hover:border-[#2A5C43] hover:shadow-md transition-all"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{need.icon}</div>
                <p className="text-sm font-semibold text-[#1C1917]">{need.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured brands */}
      <section className="py-16 bg-[#F7F2E8]/50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Marcas</p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight">Marcas destacadas</h2>
            </div>
            <Link to="/marcas" className="text-sm text-[#2A5C43] font-medium flex items-center gap-1 hover:gap-2 transition-all" data-testid="brands-see-all">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {brands.slice(0, 8).map((brand) => (
              <Link
                key={brand.id}
                to={`/marcas/${brand.slug}`}
                data-testid={`brand-${brand.slug}`}
                className="group bg-white rounded-2xl border border-[#E7E5E4] p-6 text-center hover:border-[#2A5C43] hover:shadow-md transition-all"
              >
                <div className="h-16 flex items-center justify-center mb-3">
                  <p className="font-display font-bold text-lg text-[#2A5C43] group-hover:text-[#D47A4A] transition-colors">
                    {brand.name}
                  </p>
                </div>
                <p className="text-xs text-[#57534E] line-clamp-2">{brand.positioning}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Los favoritos</p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight">Productos más vendidos</h2>
            </div>
            <Link to="/perros" className="text-sm text-[#2A5C43] font-medium flex items-center gap-1 hover:gap-2 transition-all" data-testid="bestsellers-see-all">
              Ver más <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {bestsellers.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Advice block */}
      <section className="py-16 bg-[#2A5C43] text-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-3">Asesoría personalizada</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-4">
            ¿No sabes cuál elegir?
          </h2>
          <p className="text-white/80 text-base mb-8 max-w-2xl mx-auto">
            Cuéntanos sobre tu mascota y te ayudamos a encontrar el alimento perfecto según su edad, tamaño, actividad y necesidades particulares.
          </p>
          <Button
            asChild
            className="bg-[#D47A4A] hover:bg-[#B85F35] text-white rounded-full px-10 py-6 font-semibold"
            data-testid="advice-cta-btn"
          >
            <Link to="/encuentra-alimento">
              Encontrar el alimento ideal <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Grain free */}
      {grainFree.length > 0 && (
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Nutrición ancestral</p>
                <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight">Libre de granos</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {grainFree.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="py-16 bg-[#F7F2E8]/50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: Leaf, title: 'Productos seleccionados', desc: 'Solo alimentos premium y súper premium' },
              { icon: ShieldCheck, title: 'Pago seguro', desc: 'Protegido con Bold Colombia' },
              { icon: Truck, title: 'Envíos confiables', desc: 'A todo el territorio nacional' },
              { icon: MessagesSquare, title: 'Atención personalizada', desc: 'Asesoría por WhatsApp' },
              { icon: Smartphone, title: 'Compra sencilla', desc: 'Desde celular, tablet o PC' }
            ].map((b, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-white border border-[#E7E5E4] flex items-center justify-center mx-auto mb-3">
                  <b.icon className="w-6 h-6 text-[#2A5C43]" />
                </div>
                <p className="font-display font-semibold text-sm text-[#1C1917] mb-1">{b.title}</p>
                <p className="text-xs text-[#57534E]">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-3">Novedades y promociones</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight mb-4">
            Recibe ofertas exclusivas
          </h2>
          <p className="text-[#57534E] mb-6">
            Descuentos, nuevos productos y consejos de nutrición para tu mascota
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="Tu correo electrónico"
              className="flex-1 px-5 py-3 rounded-full bg-white border border-[#E7E5E4] focus:outline-none focus:border-[#2A5C43] text-sm"
              data-testid="newsletter-email-input"
            />
            <Button
              type="submit"
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6"
              data-testid="newsletter-submit"
            >
              Suscribirme
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
